import { Link } from "react-router-dom";
import {
  Target, ShieldCheck, Sparkles, Globe2, Heart, Award, ArrowRight, Check,
  Users, Cpu, FileCheck2,
} from "lucide-react";
import { TIMELINE, VALUES } from "@/data/content";
import { PageHero } from "./Services";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

const ICONS = { Target, ShieldCheck, Sparkles, Globe2, Heart, Award };

const RARE_LANGS = [
  "Haitian Creole", "Jamaican Patois", "Wolof", "Yoruba", "and more...",
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      <Seo
        title="About Petraloc"
        description="Petraloc is a global team of certified linguists and AI specialists building the bridge between human language and intelligent automation."
        path="/about"
      />

      <PageHero
        eyebrow="About Petraloc"
        title="Where language meets intelligence."
        subtitle="Petraloc is a team of certified linguists, native domain specialists, and AI engineers — built to deliver enterprise-grade multilingual communication at the speed modern business demands."
      />

      {/* MISSION */}
      <section className="py-14 lg:py-18 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal variant="right" className="lg:col-span-5">
            <p className="eyebrow">Mission</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Language without limits.
            </h2>
            <div className="mt-6 img-zoom rounded-md overflow-hidden border border-[#E5E7EB] hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?w=1100&q=70&auto=format&fit=crop"
                alt="Petraloc team collaborating"
                className="w-full h-64 object-cover"
              />
            </div>
          </Reveal>
          <Reveal variant="left" className="lg:col-span-7 space-y-5 text-[15px] text-[#222222] leading-relaxed">
            <p>
              Petraloc was founded on a simple observation: enterprises operating across
              borders need more than translation — they need <strong className="text-[#071D49]">language intelligence</strong>.
            </p>
            <p>
              We combine native domain specialists with AI-driven quality assurance to deliver
              certified translation, localization, interpretation, and multilingual AI services
              that meet the standards of regulated industries — legal, healthcare, finance, and
              government — while remaining accessible to the underserved markets our industry typically overlooks.
            </p>
            <p>
              We are not an AI agency. We are a <strong className="text-[#071D49]">language and localization
              company</strong>, enhanced by AI.
            </p>
          </Reveal>
        </div>
      </section>

      {/* AI + HUMAN MODEL */}
      <section className="py-14 lg:py-18 bg-[#04132F] text-white relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#00D1B2]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-[#1A4E8A]/30 blur-3xl" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-10">
            <p className="eyebrow-teal">Our Model</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-white leading-[1.1]">
              AI scales. Humans decide.
            </h2>
            <p className="mt-4 text-[15px] text-white/70 leading-relaxed">
              We pair machine speed with human judgement — a hybrid architecture engineered
              for enterprise volume without sacrificing the nuance that makes language work.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-3 gap-5">
            {[
              {
                icon: Cpu,
                eyebrow: "AI Layer",
                title: "Machine speed.",
                body: "Translation memories, terminology engines, and AI-driven consistency checks process volume at scale and surface candidate drafts within minutes.",
                color: "#00D1B2",
              },
              {
                icon: Users,
                eyebrow: "Human Layer",
                title: "Native judgement.",
                body: "Domain-specialist linguists rewrite, refine, and verify every deliverable — cultural nuance, tone, and regulatory accuracy guaranteed.",
                color: "#FFFFFF",
              },
              {
                icon: FileCheck2,
                eyebrow: "Review Layer",
                title: "Independent QA.",
                body: "A second native specialist performs blind review on every project before release. Nothing ships without two human passes.",
                color: "#00D1B2",
              },
            ].map((b, i) => (
              <Reveal variant="up" delay={i + 1} key={b.title}>
                <div className="ent-card-dark p-6 lg:p-7 h-full">
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center mb-5"
                    style={{ background: `${b.color}1A`, color: b.color }}
                  >
                    <b.icon className="w-5 h-5" />
                  </div>
                  <p
                    className="text-[10.5px] tracking-[0.22em] uppercase font-semibold"
                    style={{ color: b.color }}
                  >
                    {b.eyebrow}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-white">{b.title}</h3>
                  <p className="mt-3 text-[13.5px] text-white/70 leading-relaxed">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY RARE LANGUAGES MATTER */}
      <section className="py-14 lg:py-18 bg-white">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal variant="right" className="lg:col-span-6">
            <p className="eyebrow-gold">Caribbean · African · Low-resource</p>
            <h2 className="mt-3 text-3xl lg:text-[40px] font-semibold tracking-tight text-[#071D49] leading-[1.1]">
              Why rare languages matter.
            </h2>
            <div className="mt-5 space-y-4 text-[14.5px] text-[#222222] leading-relaxed">
              <p>
                Rare languages deserve real expertise. Petraloc connects clients with
                verified native linguists across <strong className="text-[#071D49]">Caribbean,
                African, and underserved language markets</strong> — delivering precision
                where most providers fall short.
              </p>
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-y-2.5 gap-x-6">
              {RARE_LANGS.map((l) => (
                <li key={l} className="flex items-center gap-2 text-[14px] text-[#222222]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A55C]" />
                  {l}
                </li>
              ))}
            </ul>
            <Link
              to="/languages"
              className="mt-7 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1A4E8A] hover:text-[#00A990] link-ink w-fit"
            >
              Explore all 85+ languages <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </Reveal>

          <Reveal variant="left" className="lg:col-span-6">
            <div className="img-zoom relative rounded-md overflow-hidden border border-[#E5E7EB]">
              <img
                src="https://customer-assets.emergentagent.com/job_localize-intelligent/artifacts/wrtp7k5k_caribbean-business.jpg"
                alt="Caribbean cultural heritage — native language community"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071D49]/65 via-[#071D49]/15 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-white text-[11px] tracking-[0.18em] uppercase font-semibold">
                  <Globe2 className="w-4 h-4 text-[#00D1B2]" />
                  Caribbean · African · Rare
                </div>
                <span className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#C9A55C]">
                  Native specialists
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-14 lg:py-18 bg-[#F7F8FA]">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl mb-10">
            <p className="eyebrow">Values</p>
            <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-[#071D49] leading-[1.15]">
              What we hold ourselves to.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map((v, i) => {
              const Icon = ICONS[v.icon] || Target;
              return (
                <Reveal variant="up" delay={(i % 3) + 1} key={v.title}>
                  <article className="ent-card p-6 h-full">
                    <div className="w-10 h-10 rounded-md bg-[#071D49] text-white flex items-center justify-center mb-5">
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#071D49] mb-2">{v.title}</h3>
                    <p className="text-[13px] text-[#5B6472] leading-relaxed">{v.body}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="py-14 lg:py-16 bg-[#071D49] text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#00D1B2]/12 blur-3xl" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <Reveal variant="up" className="max-w-2xl">
            <p className="eyebrow-teal">Standards</p>
            <h2 className="mt-3 text-3xl lg:text-[36px] font-semibold tracking-tight text-white leading-[1.15]">
              Built to meet the world's most regulated industries.
            </h2>
          </Reveal>
          <Reveal variant="up" delay={1}>
            <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-8">
              {[
                "Secure encrypted file transfer",
                "ATA-certified linguists",
                "HIPAA-aware medical handling",
                "GDPR-compliant data processing",
                "USCIS-accepted certifications",
                "Signed NDA on every project",
              ].map((s) => (
                <li key={s} className="flex items-start gap-2 text-[14px] text-white/85">
                  <Check className="w-4 h-4 mt-0.5 text-[#00D1B2] flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
